import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpEvent, HttpHandler, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../tokens/api-config.token';

/**
 * API type enum to specify which API endpoint to use
 */
export enum ApiType {
  DEFAULT = 'default',
  OPEN = 'open',
}

@Injectable({
  providedIn: 'root',
})
export class SharedApiService {
  private apiUrl: string;
  private openApiUrl: string;
  private jobsRequest: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly http: HttpClient,
    @Inject(API_CONFIG) private readonly config: ApiConfig
  ) {
    this.apiUrl = config.apiUrl;
    this.openApiUrl = config.openApiUrl;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // add authorization header with basic auth credentials if available
    const token = localStorage.getItem('session');
    if (request.headers.get('Authorization') || request.headers.get('Token')) {
      return next.handle(request);
    }
    if (token) {
      request = request.clone({
        setHeaders: {
          Authorization: `${token}`
        }
      });
    }

    return next.handle(request);
  }


  /**
   * Get HTTP headers with authorization and client type
   */
  getHTTPHeaders(jobsRequest = false): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append('Content-Type', 'application/json');
    if (jobsRequest){
      httpHeaders = httpHeaders.append('x-client-type', 'candidate');
    }

    if (isPlatformBrowser(this.platformId)) {
      const session = localStorage.getItem('session');
      if (session) {
        httpHeaders = httpHeaders.append('Authorization', session);
      }
    }

    return httpHeaders;
  }

  /**
   * Get full API URL for an endpoint
   * @param endpoint API endpoint
   * @param apiType Which API endpoint to use (default or open)
   */
  getApiUrl(endpoint: string, apiType: ApiType = ApiType.DEFAULT): string {
    const baseUrl = apiType === ApiType.OPEN ? this.openApiUrl : this.apiUrl;
    var url = `${baseUrl}${endpoint}`;
    return url;
  }

  /**
   * Make a GET request to the API
   * @param endpoint API endpoint
   * @param params Query parameters
   * @param options Additional options
   */
  get<T>(endpoint: string, params?: HttpParams, options?: any): Observable<T> {
    const apiType = options?.apiType || ApiType.DEFAULT;
    const url = this.getApiUrl(endpoint, apiType);
    const headers = this.getHTTPHeaders(options?.jobsRequest);

    // Remove our custom option before passing to HttpClient
    if (options?.apiType !== undefined) {
      const { apiType, ...restOptions } = options;
      options = restOptions;
    }

    return this.http.get<T>(url, {
      headers,
      params,
      ...options,
    }) as Observable<T>;
  }

  /**
   * Make a POST request to the API
   * @param endpoint API endpoint
   * @param body Request body
   * @param params Query parameters
   * @param options Additional options
   */
  post<T>(endpoint: string, body: any, params?: HttpParams, options?: any): Observable<T> {
    const apiType = options?.apiType || ApiType.DEFAULT;
    const url = this.getApiUrl(endpoint, apiType);
    const headers = this.getHTTPHeaders(options?.jobsRequest);

    // Remove our custom option before passing to HttpClient
    if (options?.apiType !== undefined) {
      const { apiType, ...restOptions } = options;
      options = restOptions;
    }

    return this.http.post<T>(url, body, {
      headers,
      params,
      ...options,
    }) as Observable<T>;
  }

  /**
   * Make a PUT request to the API
   * @param endpoint API endpoint
   * @param body Request body
   * @param params Query parameters
   * @param options Additional options
   */
  put<T>(endpoint: string, body: any, params?: HttpParams, options?: any): Observable<T> {
    const apiType = options?.apiType || ApiType.DEFAULT;
    const url = this.getApiUrl(endpoint, apiType);
    const headers = this.getHTTPHeaders(options?.jobsRequest);

    // Remove our custom option before passing to HttpClient
    if (options?.apiType !== undefined) {
      const { apiType, ...restOptions } = options;
      options = restOptions;
    }

    return this.http.put<T>(url, body, {
      headers,
      params,
      ...options,
    }) as Observable<T>;
  }

  /**
   * Make a DELETE request to the API
   * @param endpoint API endpoint
   * @param params Query parameters
   * @param options Additional options
   */
  delete<T>(endpoint: string, params?: HttpParams, options?: any): Observable<T> {
    const apiType = options?.apiType || ApiType.DEFAULT;
    const url = this.getApiUrl(endpoint, apiType);
    const headers = this.getHTTPHeaders(options?.jobsRequest);

    // Remove our custom option before passing to HttpClient
    if (options?.apiType !== undefined) {
      const { apiType, ...restOptions } = options;
      options = restOptions;
    }

    return this.http.delete<T>(url, {
      headers,
      params,
      ...options,
    }) as Observable<T>;
  }
}
